import {clsx} from 'clsx';
import React from 'react';

import {parseRef} from '../../../../react';
import {TailwindContents} from '../../../Tailwind';
import {isArtifactRef, isWeaveRef} from '../Browse3/filters/common';
import {ValueViewNumber} from '../Browse3/pages/CallPage/ValueViewNumber';
import {
  isProbablyTimestamp,
  ValueViewNumberTimestamp,
} from '../Browse3/pages/CallPage/ValueViewNumberTimestamp';
import {ValueViewPrimitive} from '../Browse3/pages/CallPage/ValueViewPrimitive';
import {SmallRef} from '../Browse3/smallRef/SmallRef';
import {isCustomWeaveTypePayload} from '../Browse3/typeViews/customWeaveType.types';
import {CustomWeaveTypeDispatcher} from '../Browse3/typeViews/CustomWeaveTypeDispatcher';
import {CellValueBoolean} from './CellValueBoolean';
import {CellValueImage} from './CellValueImage';
import {CellValueNumberList} from './CellValueNumberList';
import {CellValueString, ViewMode} from './CellValueString';

type CellValueProps = {
  value: any;
  noLink?: boolean;
  /** Optional style overrides for string values */
  stringStyle?: React.CSSProperties;
  field?: string;
  /**
   * Column-wide view-mode key for numeric-list and string cells. Falls back to
   * `field`; prefer the grid's `params.field` so it's unique.
   */
  columnKey?: string;
  /**
   * Default view mode for complex values.
   * 'Text' - shows as text with popup (default)
   * 'Code' - shows in code viewer with JSON formatting
   * 'Markdown' - shows as rendered markdown
   */
  defaultViewMode?: ViewMode;
  /**
   * Force a single horizontal alignment for the value, overriding the per-type
   * default.
   */
  alignOverride?: 'left' | 'center' | 'right';
  /**
   * The surrounding table gives cells extra vertical space, so value types that
   * can use it render a richer form.
   */
  tall?: boolean;
  /** Accent color for numeric-list plots, e.g. the column's eval color. */
  plotColor?: string;
  /**
   * Opt in to charting numeric lists, with a Text/Code toggle, instead of the
   * plain JSON-string rendering. Off by default; only the eval comparison table
   * sets it.
   */
  plotNumberLists?: boolean;
};

export const CellValue = ({
  value,
  noLink,
  stringStyle,
  field,
  columnKey,
  defaultViewMode = 'Text',
  alignOverride,
  tall,
  plotColor,
  plotNumberLists,
}: CellValueProps) => {
  // Shared by the numeric-list and string cells for their column-wide view
  // mode; each namespaces the key by its own cell type (see columnViewMode).
  const effectiveColumnKey = columnKey ?? field;
  if (value === undefined) {
    return null;
  }
  if (value === null) {
    return <ValueViewPrimitive>null</ValueViewPrimitive>;
  }
  if (isWeaveRef(value) || isArtifactRef(value)) {
    return <SmallRef objRef={parseRef(value)} noLink={noLink} />;
  }
  if (typeof value === 'boolean') {
    return (
      <TailwindContents>
        <div
          className={clsx(
            'h-full w-full',
            alignClass(alignOverride, 'center')
          )}>
          <CellValueBoolean value={value} />
        </div>
      </TailwindContents>
    );
  }
  if (typeof value === 'string') {
    if (value.startsWith('data:image/')) {
      return <CellValueImage value={value} />;
    }
    return (
      <CellValueString
        value={value}
        style={
          alignOverride
            ? {...stringStyle, textAlign: alignOverride}
            : stringStyle
        }
        defaultViewMode={defaultViewMode}
        columnKey={effectiveColumnKey}
      />
    );
  }
  if (typeof value === 'number') {
    if (field && isProbablyTimestamp(value, field)) {
      return <ValueViewNumberTimestamp value={value} />;
    }
    return (
      <TailwindContents>
        <div className={clsx('w-full', alignClass(alignOverride, 'right'))}>
          <ValueViewNumber value={value} fractionDigits={4} />
        </div>
      </TailwindContents>
    );
  }
  if (isCustomWeaveTypePayload(value)) {
    return <CustomWeaveTypeDispatcher data={value} />;
  }
  if (plotNumberLists && isNumberArray(value)) {
    return (
      <TailwindContents>
        <div
          className={clsx(
            'w-full',
            tall && 'h-full',
            alignClass(alignOverride, 'right')
          )}>
          <CellValueNumberList
            value={value}
            columnKey={effectiveColumnKey}
            color={plotColor}
            tall={tall}
          />
        </div>
      </TailwindContents>
    );
  }

  // For complex values, determine view mode automatically if not specified
  const effectiveViewMode: ViewMode =
    defaultViewMode || (typeof value === 'object' ? 'Code' : 'Text');

  return (
    <CellValueString
      value={JSON.stringify(value)}
      style={
        alignOverride ? {...stringStyle, textAlign: alignOverride} : stringStyle
      }
      defaultViewMode={effectiveViewMode}
      columnKey={effectiveColumnKey}
    />
  );
};

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(v => typeof v === 'number' && Number.isFinite(v));

const alignClass = (
  override: CellValueProps['alignOverride'],
  fallback: 'center' | 'right'
): string => {
  switch (override ?? fallback) {
    case 'left':
      return 'text-left';
    case 'right':
      return 'text-right';
    case 'center':
      return 'text-center';
  }
};
